import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useProjectRetainage, useProjectRetainageReleases } from "@/hooks/useRetainageReleases";
import { MarginAlertBadge } from "./MarginAlertBadge";
import { VarianceExplanationCard } from "./VarianceExplanationCard";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

import type { ProjectAlert } from "@/hooks/useProjectAlerts";

interface Props {
  projectId: string;
  contractValue: number;
  estimatedCost: number;
  committedCost: number;
  actualCost: number;
  pendingPOs: number;
  billedToDate: number;
  collectedToDate: number;
  pendingInvoices: number;
  estimatedMargin: number;
  margin: number;
  actualCostMargin: number;
  teamCount: number;
  alerts?: ProjectAlert[];
}

export const ProjectOverviewTab = ({
  projectId,
  contractValue,
  estimatedCost,
  committedCost,
  actualCost,
  billedToDate,
  collectedToDate,
  pendingInvoices,
  estimatedMargin,
  margin,
  actualCostMargin,
  teamCount,
  alerts = [],
}: Props) => {
  const { data: workOrders } = useProjectRetainage(projectId);
  const { data: releases } = useProjectRetainageReleases(projectId);

  const totalHeld = (workOrders ?? []).reduce((s: number, wo: any) => {
    const held = Number(wo.amount) * (Number(wo.retainage_pct ?? 10) / 100);
    return s + held;
  }, 0);
  const totalReleased = (workOrders ?? []).reduce(
    (s: number, wo: any) => s + Number(wo.retainage_released ?? 0),
    0
  );
  const retainageBalance = totalHeld - totalReleased;
  const hasPendingRelease = (releases ?? []).some((r: any) => r.status === "pending");

  const marginColor = (v: number) =>
    v >= 20 ? "text-green-600" : v >= 10 ? "text-yellow-600" : "text-red-600";

  const rows = [
    { label: "Contract Value", value: fmt(contractValue) },
    { label: "Estimated Cost", value: fmt(estimatedCost) },
    { label: "WO Committed Cost", value: fmt(committedCost) },
    { label: "Actual Cost (All Approved Expenses)", value: fmt(actualCost) },
    { label: "Total Invoiced", value: fmt(billedToDate) },
    { label: "Collected", value: fmt(collectedToDate) },
    { label: "Total Pending Invoices", value: fmt(pendingInvoices) },
  ];

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
            Financial Summary
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metric</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.label} className={i % 2 === 1 ? "bg-muted/30" : ""}>
                  <TableCell className="font-medium">{r.label}</TableCell>
                  <TableCell className="text-right">{r.value}</TableCell>
                </TableRow>
              ))}
              {totalHeld > 0 && (
                <TableRow className="bg-muted/30">
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      Retainage Held
                      {hasPendingRelease && (
                        <Badge variant="outline" className="text-[10px]">Release Pending</Badge>
                      )}
                      {totalReleased > 0 && retainageBalance === 0 && (
                        <Badge variant="secondary" className="text-[10px]">Fully Released</Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">{fmt(retainageBalance)}</TableCell>
                </TableRow>
              )}
              {/* Margin rows */}
              <TableRow className="border-t-2">
                <TableCell className="font-semibold">Margin (Estimated)</TableCell>
                <TableCell className={`text-right font-semibold ${marginColor(estimatedMargin)}`}>
                  {estimatedMargin.toFixed(1)}%
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-semibold">
                  <span className="flex items-center gap-2">
                    Margin (Committed)
                    <MarginAlertBadge estimatedMarginPct={estimatedMargin} currentMarginPct={margin} label="committed" />
                  </span>
                </TableCell>
                <TableCell className={`text-right font-semibold ${marginColor(margin)}`}>
                  {margin.toFixed(1)}%
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-semibold">
                  <span className="flex items-center gap-2">
                    Margin (Actual)
                    <MarginAlertBadge estimatedMarginPct={estimatedMargin} currentMarginPct={actualCostMargin} label="actual" />
                  </span>
                </TableCell>
                <TableCell className={`text-right font-semibold ${marginColor(actualCostMargin)}`}>
                  {actualCostMargin.toFixed(1)}%
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <VarianceExplanationCard projectId={projectId} alerts={alerts} />
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
            Team Members
          </h3>
          <p className="text-sm">{teamCount} assigned</p>
        </div>
      </div>
    </TooltipProvider>
  );
};
